import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, CardHeader, CardBody, Row, Col, Button, Table, FormGroup, Label, Input } from "reactstrap";
import axios from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function rowResults(r) {
  const actions = Array.isArray(r.actions) ? r.actions : [];
  const actionPriority = ["purchase", "offsite_conversion.purchase", "lead", "add_to_cart", "link_click"];
  for (const type of actionPriority) {
    const hit = actions.find((a) => a.action_type === type);
    if (hit) return num(hit.value);
  }
  return actions.length ? num(actions[0].value) : 0;
}

const MetaCampaignDetail = () => {
  const { adAccountId, campaignId } = useParams();
  const history = useHistory();
  const { token: authToken } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [datePreset, setDatePreset] = useState("last_7_days");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!authToken || !adAccountId || !campaignId) return;
    setLoading(true);
    axios
      .get(`/ad-accounts/${adAccountId}/campaigns?campaign_id=${encodeURIComponent(campaignId)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const row = (res.data.data || [])[0] || null;
        setCampaign(row);
      })
      .catch((e) => toast.error(e.response?.data?.message || "Failed to load campaign detail"))
      .finally(() => setLoading(false));
  }, [authToken, adAccountId, campaignId]);

  const loadInsights = () => {
    if (!authToken || !adAccountId || !campaignId) return;
    setInsightsLoading(true);
    axios
      .get(
        `/meta/insights/campaign/${encodeURIComponent(campaignId)}?adAccountId=${encodeURIComponent(
          adAccountId
        )}&date_preset=${encodeURIComponent(datePreset)}&time_increment=1`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => setRows(res.data.data || []))
      .catch((e) => toast.error(e.response?.data?.message || "Failed to load campaign insights"))
      .finally(() => setInsightsLoading(false));
  };

  useEffect(() => {
    loadInsights();
  }, [authToken, adAccountId, campaignId, datePreset]);

  const totals = rows.reduce(
    (t, r) => {
      t.impressions += num(r.impressions);
      t.reach += num(r.reach);
      t.clicks += num(r.clicks);
      t.spend += num(r.spend);
      t.results += rowResults(r);
      return t;
    },
    { impressions: 0, reach: 0, clicks: 0, spend: 0, results: 0 }
  );
  const ctr = totals.impressions ? (totals.clicks / totals.impressions) * 100 : 0;
  const cpc = totals.clicks ? totals.spend / totals.clicks : 0;

  return (
    <div className="animated fadeIn">
      <ToastContainer autoClose={5000} />
      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <b>Meta Campaign Detail</b>
              <Button color="secondary" size="sm" onClick={() => history.push("/seller/social-advertisement")}>
                Back
              </Button>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-4">
                  <i className="fas fa-spinner fa-pulse fa-2x" aria-hidden />
                </div>
              ) : !campaign ? (
                <p className="text-muted">Campaign not found.</p>
              ) : (
                <>
                  <Table bordered striped responsive size="sm">
                    <tbody>
                      <tr><th width="220">Name</th><td>{campaign.name || "-"}</td></tr>
                      <tr><th>Campaign ID</th><td style={{ fontFamily: "monospace" }}>{campaign.id || "-"}</td></tr>
                      <tr><th>Status</th><td>{campaign.status || "-"}</td></tr>
                      <tr><th>Objective</th><td>{campaign.objective || "-"}</td></tr>
                      <tr><th>Created</th><td>{campaign.created_time ? new Date(campaign.created_time).toLocaleString() : "-"}</td></tr>
                    </tbody>
                  </Table>

                  <Row form className="align-items-end mb-3">
                    <Col md={6}>
                      <FormGroup>
                        <Label>Period</Label>
                        <Input type="select" value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
                          <option value="last_7_days">Last 7 days</option>
                          <option value="last_14_days">Last 14 days</option>
                          <option value="last_30_days">Last 30 days</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6} className="text-right">
                      <Button color="secondary" size="sm" onClick={loadInsights} disabled={insightsLoading}>
                        {insightsLoading ? "Refreshing..." : "Refresh insights"}
                      </Button>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={2}>
                      <Card body>
                        <b>Impressions</b>
                        <div>{totals.impressions.toLocaleString()}</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>Clicks</b>
                        <div>{totals.clicks.toLocaleString()}</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>CTR</b>
                        <div>{ctr.toFixed(2)}%</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>Spend</b>
                        <div>${totals.spend.toFixed(2)}</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>CPC</b>
                        <div>${cpc.toFixed(2)}</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>Results</b>
                        <div>{totals.results.toLocaleString()}</div>
                      </Card>
                    </Col>
                  </Row>

                  <Card>
                    <CardHeader><b>Daily insights</b></CardHeader>
                    <CardBody>
                      <Table bordered striped responsive size="sm">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Impressions</th>
                            <th>Reach</th>
                            <th>Clicks</th>
                            <th>Spend</th>
                            <th>Results</th>
                          </tr>
                        </thead>
                        <tbody>
                          {insightsLoading ? (
                            <tr>
                              <td colSpan={6} className="py-4 text-center">
                                <i className="fas fa-spinner fa-pulse fa-2x" aria-hidden />
                              </td>
                            </tr>
                          ) : rows.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="py-4 text-center text-muted">
                                No insights data.
                              </td>
                            </tr>
                          ) : (
                            rows.map((r, idx) => (
                              <tr key={idx}>
                                <td>{r.date_start || "-"}</td>
                                <td>{num(r.impressions).toLocaleString()}</td>
                                <td>{num(r.reach).toLocaleString()}</td>
                                <td>{num(r.clicks).toLocaleString()}</td>
                                <td>${num(r.spend).toFixed(2)}</td>
                                <td>{rowResults(r).toLocaleString()}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MetaCampaignDetail;

